import {useMemo} from 'react';
import {parseGidType} from '@web-utilities/other';
import {I18n, useI18n} from '@shopify/react-i18n';
import {TranslatableResourceType} from 'types';
import type {
  TranslatableEntry,
  TranslatableResourceTypeMapping,
} from 'features/Translations/TranslatableResource/types';
import {
  getHumanReadableTranslatableThemeKey,
  isTranslatableResourceWithHandleTranslation,
} from 'features/Translations/TranslatableResource/utils';
import {Market} from 'utilities/use-shop-locales-and-markets';

import {
  TranslatableResource,
  TranslatableResourceInformation,
  MarketLocalizableResource,
  TranslatableResourceInformationOverrides,
} from '../../types';
import {isMetafieldOverride, isOptionsOverride} from '../../utilities';
import {TranslatableResourcesByIdsQueryData} from '../../graphql/TranslatableResourcesByIdsQuery.graphql';

import {sortEntriesForDisplay} from './utils';

interface Props {
  translatableResources: TranslatableResource[];
  marketLocalizableResources: MarketLocalizableResource[];
  market?: Market;
  localeCode?: string;
}

const SUBTITLE_TRANSLATION_KEY: TranslatableResourceTypeMapping<string> = {
  [TranslatableResourceType.Metafield]: 'metafield',
  [TranslatableResourceType.ProductOptionValue]: 'productOptionValue',

  [TranslatableResourceType.Link]: 'link',
};

function getDisplayName(
  key: string,
  translatableResourceType: TranslatableResourceType,
  overrides?: TranslatableResourceInformation['overrides'],
) {
  if (
    translatableResourceType === TranslatableResourceType.Metafield &&
    isMetafieldOverride(overrides)
  ) {
    return overrides?.name;
  }

  // This is temporary until we have localize Theme Sections
  // directly in the Theme Editor
  if (
    [
      TranslatableResourceType.OnlineStoreThemeJsonTemplate,
      TranslatableResourceType.OnlineStoreThemeSectionGroup,
      TranslatableResourceType.OnlineStoreThemeSettingsCategory,
      TranslatableResourceType.OnlineStoreThemeLocaleContent,
      TranslatableResourceType.OnlineStoreThemeSettingsDataSections,
    ].includes(translatableResourceType)
  ) {
    return getHumanReadableTranslatableThemeKey(key);
  }

  return undefined;
}

function buildCommonFields(
  i18n: I18n,
  translatableResourceType: TranslatableResourceType,
  key: string,
  overrides?: TranslatableResourceInformationOverrides,
) {
  let displayName = getDisplayName(key, translatableResourceType, overrides);

  let metafieldDetails: TranslatableEntry['metafieldDetails'] | undefined;
  if (isMetafieldOverride(overrides)) {
    metafieldDetails = {
      name: overrides.name,
      namespace: overrides.namespace,
      key: overrides.key,
      ownerType: overrides.ownerType,
      type: overrides.type,
      validations: overrides.validations,
    };
  } else if (
    translatableResourceType === TranslatableResourceType.Metaobject &&
    Array.isArray(overrides)
  ) {
    const metaobjectOverride = overrides?.find(
      (override) => override.key === key,
    );

    if (metaobjectOverride) {
      const {name: title, type, validations} = metaobjectOverride;
      displayName = title;
      metafieldDetails = {
        type,
        validations,
        ownerType: '',
        namespace: '',
        key: '',
        name: '',
      };
    }
  }
  const subtitle = getSubtitle(
    translatableResourceType,
    i18n,
    metafieldDetails?.ownerType,
  );
  const display = {
    title: displayName,
    subtitle,
    position: isOptionsOverride(overrides) ? overrides.position : undefined,
  };

  return {
    display,
    metafieldDetails,
  };
}

function getSubtitle(
  translatableResourceType: TranslatableResourceType,
  i18n: I18n,
  ownerType?: string,
) {
  const scope = 'TranslatableEntries.subtitle';

  let translationKey = SUBTITLE_TRANSLATION_KEY[translatableResourceType] ?? '';

  if (translatableResourceType === TranslatableResourceType.Metafield) {
    switch (ownerType) {
      case 'PRODUCT':
        translationKey = 'metafield.product';
        break;
      case 'PRODUCTVARIANT':
        translationKey = 'metafield.productVariant';
        break;
      default:
        translationKey = 'metafield.default';
    }
  }

  if (i18n.translationKeyExists(`${scope}.${translationKey}`)) {
    if (
      i18n.translationKeyExists(`${scope}.${translationKey}.one`) &&
      i18n.translationKeyExists(`${scope}.${translationKey}.other`)
    ) {
      return {
        singular: i18n.translate(`${translationKey}.one`, {scope}),
        plural: i18n.translate(`${translationKey}.other`, {scope}),
      };
    }
    return {
      singular: i18n.translate(translationKey, {scope}),
    };
  }
  return undefined;
}

function createEntry({
  defaultContent,
  i18n,
  translatableResource,
  isContextualizedThemeContent,
}: {
  defaultContent: TranslatableResourcesByIdsQueryData.TranslatableResourcesByIdsDefaultContent;
  i18n: I18n;
  translatableResource: TranslatableResource;
  isContextualizedThemeContent: boolean;
}) {
  const {
    translatedContent: translatableResourceTranslatedContent,
    translatedMarketContent: translatableResourceTranslatedMarketContent,
    resourceId,
    overrides,
  } = translatableResource;

  const key = defaultContent.key;
  const gidKey = `${resourceId}/${key}`;
  const gidType = parseGidType(resourceId);
  const translatedContent = translatableResourceTranslatedContent.find(
    (content) => content.key === key,
  );
  const translatedMarketContent =
    translatableResourceTranslatedMarketContent?.find(
      (content) => content.key === key,
    );
  const translatableResourceType =
    TranslatableResourceType[gidType as keyof typeof TranslatableResourceType];
  const {display, metafieldDetails} = buildCommonFields(
    i18n,
    translatableResourceType,
    key,
    overrides,
  );

  const displayOverrides: {
    readOnlyPrefix?: string;
    prefix?: string;
    placeholder?: string;
  } = {};
  if (
    isTranslatableResourceWithHandleTranslation(translatableResourceType, key)
  ) {
    displayOverrides.placeholder = defaultContent.value || '';
  }

  const translatableEntry = {
    gidKey,
    key,
    resourceId,
    translatableResourceType,
    defaultContent,
    translatedContent,
    translatedMarketContent,
    display: {
      ...display,
      ...displayOverrides,
    },
    metafieldDetails,
    isOnlyMarketLocalizable: false,
    isContextualizedThemeContent,
  };

  return translatableEntry;
}

export function transformToTranslatableEntries({
  translatableResource,
  i18n,
}: {
  translatableResource: TranslatableResource;
  i18n: I18n;
}) {
  const {defaultContent: translatableResourceDefaultContent} =
    translatableResource;

  return translatableResourceDefaultContent.map((defaultContent) =>
    createEntry({
      defaultContent,
      i18n,
      translatableResource,
      isContextualizedThemeContent: false,
    }),
  );
}

export function transformToContextualizedTranslatableEntries({
  translatableResource,
  i18n,
}: {
  translatableResource: TranslatableResource;
  i18n: I18n;
}) {
  const {contextualizedTranslatableContent} = translatableResource;

  if (!contextualizedTranslatableContent) return [];
  return contextualizedTranslatableContent.map((defaultContent) =>
    createEntry({
      defaultContent,
      i18n,
      translatableResource,
      isContextualizedThemeContent: true,
    }),
  );
}

export function transformMarketLocalizationToTranslatableEntries({
  marketLocalizableResource,
  i18n,
  market,
}: {
  marketLocalizableResource: MarketLocalizableResource;
  i18n: I18n;
  market?: Market;
}) {
  const {
    marketLocalizableContent: marketLocalizableResourceDefaultContent,
    marketLocalizations: marketLocalizableResourceTranslatedContent,
    resourceId,
    overrides,
  } = marketLocalizableResource;

  return marketLocalizableResourceDefaultContent.map((defaultContent) => {
    const key = defaultContent.key;
    const gidKey = `${resourceId}/${key}`;
    const marketLocalizableTranslatedContent =
      marketLocalizableResourceTranslatedContent.find(
        (content) => content.key === key,
      );

    const gidType = parseGidType(resourceId);
    const translatableResourceType =
      TranslatableResourceType[
        gidType as keyof typeof TranslatableResourceType
      ];
    const {display, metafieldDetails} = buildCommonFields(
      i18n,
      translatableResourceType,
      key,
      overrides,
    );

    let defaultContentValueOverride;
    let marketLocalizableTranslatedContentValueOverride;

    const displayOverrides: {readOnlySuffix?: string; suffix?: string} = {};
    if (
      [
        TranslatableResourceType.Metafield,
        TranslatableResourceType.Metaobject,
      ].includes(translatableResourceType) &&
      metafieldDetails?.type === 'money' &&
      defaultContent.value
    ) {
      const currencyInformation = JSON.parse(defaultContent.value);
      defaultContentValueOverride = currencyInformation.amount;
      displayOverrides.readOnlySuffix = currencyInformation.currency_code;

      if (marketLocalizableTranslatedContent?.value) {
        const translatedCurrencyInformation = JSON.parse(
          marketLocalizableTranslatedContent.value,
        );

        marketLocalizableTranslatedContentValueOverride =
          translatedCurrencyInformation.amount;
        displayOverrides.suffix = translatedCurrencyInformation.currency_code;
      } else {
        displayOverrides.suffix =
          market?.currencySettings?.baseCurrency?.currencyCode;
      }
    }

    const marketLocalizableTranslatedEntry = {
      gidKey,
      key,
      resourceId,
      translatableResourceType,
      defaultContent: {
        ...defaultContent,
        value: defaultContentValueOverride || defaultContent?.value,
      },
      translatedContent: undefined,
      translatedMarketContent: marketLocalizableTranslatedContent
        ? {
            ...marketLocalizableTranslatedContent,
            value:
              marketLocalizableTranslatedContentValueOverride ||
              marketLocalizableTranslatedContent?.value,
          }
        : undefined,
      display: {
        ...display,
        ...displayOverrides,
      },
      metafieldDetails,
      isOnlyMarketLocalizable: true,
      isContextualizedThemeContent: false,
    };

    return marketLocalizableTranslatedEntry;
  });
}

export function useTranslatableEntriesFromResources({
  translatableResources,
  marketLocalizableResources,
  market,
}: Props) {
  const [i18n] = useI18n();

  return useMemo<TranslatableEntry[]>(() => {
    const translatableEntries = translatableResources.flatMap(
      (translatableResource) =>
        transformToTranslatableEntries({translatableResource, i18n}),
    );
    const contextualizedThemeTranslatableEntries =
      translatableResources.flatMap((translatableResource) =>
        transformToContextualizedTranslatableEntries({
          translatableResource,
          i18n,
        }),
      );
    const marketLocalizableTranslatableEntries =
      marketLocalizableResources.flatMap((marketLocalizableResource) =>
        transformMarketLocalizationToTranslatableEntries({
          marketLocalizableResource,
          i18n,
          market,
        }),
      );

    return [
      ...sortEntriesForDisplay(translatableEntries),
      ...sortEntriesForDisplay(marketLocalizableTranslatableEntries),
      ...sortEntriesForDisplay(contextualizedThemeTranslatableEntries),
    ];
  }, [translatableResources, marketLocalizableResources, i18n, market]);
}
